import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Form, Input, Button, Card, message, Typography, Progress } from 'antd'
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons'

const { Title, Text } = Typography

const API_URL = (import.meta.env as any).VITE_API_URL || 'http://localhost:3001/api'

export const ResetPassword: React.FC = () => {
  const [form] = Form.useForm()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [resetSuccess, setResetSuccess] = useState(false)
  
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      message.error('Invalid reset link')
      navigate('/login')
    }
  }, [token, navigate])

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0
    if (password.length >= 8) strength += 25
    if (password.length >= 12) strength += 25
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25
    if (/\d/.test(password)) strength += 12.5
    if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5
    return Math.min(strength, 100)
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value
    setPasswordStrength(calculatePasswordStrength(password))
  }

  const getStrengthColor = () => {
    if (passwordStrength < 50) return '#ff4d4f'
    if (passwordStrength < 75) return '#faad14'
    return '#52c41a'
  }

  const getStrengthText = () => {
    if (passwordStrength < 50) return 'Weak'
    if (passwordStrength < 75) return 'Good'
    return 'Strong'
  }

  const onFinish = async (values: { newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('Passwords do not match')
      return
    }

    if (values.newPassword.length < 8) {
      message.error('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: values.newPassword
        }),
      })

      const data = await response.json()

      if (response.ok) {
        setResetSuccess(true)
        message.success('Password reset successfully!')
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        message.error(data.message || 'Failed to reset password')
      }
    } catch (error) {
      console.error('Reset password error:', error)
      message.error('Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (resetSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        padding: '20px'
      }}>
        <Card
          style={{
            maxWidth: 500,
            width: '100%',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: '#52c41a',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircleOutlined style={{ fontSize: 40, color: '#ffffff' }} />
            </div>
            
            <Title level={2} style={{ marginBottom: 16 }}>
              Password Reset Successfully!
            </Title>
            
            <Text style={{ fontSize: 16, color: '#666', display: 'block', marginBottom: 24 }}>
              Your admin password has been changed. You can now log in with your new password.
            </Text>
            
            <Text style={{ fontSize: 14, color: '#999', display: 'block', marginBottom: 24 }}>
              Redirecting to login...
            </Text>
            
            <Link to="/login">
              <Button type="primary" size="large">
                Go to Login
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      padding: '20px'
    }}>
      <Card
        style={{
          maxWidth: 450,
          width: '100%',
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            Reset Admin Password
          </Title>
          <Text style={{ fontSize: 16, color: '#666' }}>
            Enter your new password below
          </Text>
        </div>

        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          size="large"
          requiredMark={false}
        >
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#999' }} />}
              placeholder="Enter new password"
              onChange={handlePasswordChange}
              autoComplete="new-password"
            />
          </Form.Item>

          {form.getFieldValue('newPassword') && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text style={{ fontSize: 14, color: '#666' }}>Password Strength:</Text>
                <Text style={{ fontSize: 14, color: getStrengthColor(), fontWeight: 600 }}>
                  {getStrengthText()}
                </Text>
              </div>
              <Progress
                percent={passwordStrength}
                strokeColor={getStrengthColor()}
                showInfo={false}
                size="small"
              />
            </div>
          )}

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Passwords do not match'))
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#999' }} />}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{
                height: 48,
                fontSize: 16,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                border: 'none'
              }}
            >
              Reset Password
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: 14, color: '#999' }}>
              Remember your password?{' '}
              <Link to="/login" style={{ fontWeight: 600 }}>
                Log in
              </Link>
            </Text>
          </div>
        </Form>
      </Card>
    </div>
  )
}

export default ResetPassword

